import { useEffect, useState } from 'react';
import {
  loadActiveFileHandle,
  getActiveArchiveFileName,
  readArchiveManifest,
  archiveWithOverwriteCheck,
  startArchiveChannel,
  stopArchiveChannel,
} from '../utils/archiveApi';
import { showToast } from '../utils/alerts-messages';
import { LIFECYCLE_READY } from '../utils/lifecycleUtils';

/**
 * Archive support (refactor Step 4): the open local archive file, the
 * write-back channel to the archive server, and the archive actions.
 * All logic moved verbatim from App.js.
 *
 * Params:
 *  - surveys, surveyStatusById: from useSurveyData (WP3 §7 — only Ready
 *    surveys may be archived).
 *  - refresh: from useSurveyData.
 *  - setLoading, onError, setConfirmModal: App-global UI.
 *  - openAccountPanel(section): App bridge to open Accounts → Archives.
 *
 * Returns state for App to render plus the actions:
 *  - archiveFileOpen / archiveHasContent (+ setters, driven by AccountPanel's
 *    onArchiveStateChange when the user opens/closes an archive file).
 *  - archiveProgress: { current, total, archived, failed } during archiving.
 *  - overwriteModal, setOverwriteModal: the §A4 overwrite-consent dialog —
 *    { collisions, filename, resolve } while awaiting the author's decision.
 */
export default function useArchive({ surveys, surveyStatusById, refresh, setLoading, onError, setConfirmModal, openAccountPanel }) {
  const [archiveFileOpen, setArchiveFileOpen] = useState(false);
  const [archiveHasContent, setArchiveHasContent] = useState(false);
  const [archiveProgress, setArchiveProgress] = useState(null); // { current, total, archived, failed } during bulk survey archive
  // Overwrite-consent dialog for contextual archiving (archiver UX §A4). Set to
  // { collisions, filename, resolve } while awaiting the author's decision.
  const [overwriteModal, setOverwriteModal] = useState(null);

  // Restore the active archive file handle from IndexedDB on mount
  useEffect(() => {
    (async () => {
      try {
        const handle = await loadActiveFileHandle();
        if (handle) {
          const perm = await handle.queryPermission({ mode: 'readwrite' });
          if (perm === 'granted') {
            setArchiveFileOpen(!!getActiveArchiveFileName());
            try {
              const manifest = await readArchiveManifest();
              setArchiveHasContent(manifest && manifest.surveys && manifest.surveys.length > 0);
            } catch (_) {
              setArchiveHasContent(false);
            }
          }
        }
      } catch (_) { /* no stored handle */ }
    })();
  }, []);

  // Session channel to the archive server: while an archive file is open, the
  // console is the one local writer. Merges submitted from anywhere (this
  // console, the explorer) are announced on the channel and written back to
  // the local file idempotently (by checksum). Lifetime = file-open lifetime.
  useEffect(() => {
    if (!archiveFileOpen) return undefined;
    startArchiveChannel({
      onWriteBack: ({ written, event }) => {
        if (written) {
          setArchiveHasContent(true);
          if (event?.type === 'merge' || event?.type === 'adopt') {
            showToast('Archive file updated', 'info');
          }
        }
      },
      onError: (err) => {
        onError(`Archive write-back failed: ${err.message}. Re-open the archive file in Accounts → Archives.`);
      },
    });
    return () => stopArchiveChannel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveFileOpen]);

  // Styled confirmation for the archive resync recovery path. Resolves true
  // when the user agrees to re-base the server staging area on the open local
  // archive file and retry the archive.
  const confirmArchiveResync = () => new Promise((resolve) => {
    setConfirmModal({
      title: 'Archive out of sync',
      message: 'The server’s staging area does not match the open archive file. This can happen after an interrupted archive run or after switching archive files. Resync the server to this file and retry?',
      confirmLabel: 'Resync & Retry',
      onConfirm: () => { setConfirmModal(null); resolve(true); },
      onCancel: () => resolve(false),
    });
  });

  // Author-consent gate for overwriting artifacts already in the open archive
  // (archiver UX §A4). Resolves true to replace, false to cancel.
  const confirmArchiveOverwrite = (collisions, filename) => new Promise((resolve) => {
    setOverwriteModal({ collisions, filename, resolve });
  });

  const onArchiveSurvey = async (surveyId) => {
    if (!archiveFileOpen) {
      onError("No archive file open. Please go to Accounts → Archives and open an archive file first.");
      openAccountPanel('archive');
      return;
    }

    // WP3 §7: archiving requires the survey be Ready. A Deployed / Staged / Sent
    // survey has a live deployment whose response set is still mutable, so the
    // archive is refused with a plain explanation (the archive-server enforces
    // this too).
    const lifecycle = surveyStatusById.get(surveyId);
    if (lifecycle && lifecycle !== LIFECYCLE_READY) {
      const survey = (surveys || []).find(s => s.survey_id === surveyId);
      onError(`Cannot archive "${survey?.name || surveyId}": the survey is ${lifecycle}. Undeploy it first — only Ready surveys can be archived.`);
      return;
    }

    setLoading(true);
    onError('');
    setArchiveProgress({ current: 0, total: 1, archived: 0, failed: 0 });

    try {
      await archiveWithOverwriteCheck({ survey_ids: [surveyId] }, (p) => {
        setArchiveProgress({ current: 0, total: 1, archived: p.percent === 100 ? 1 : 0, failed: 0 });
      }, { confirmOverwrite: confirmArchiveOverwrite, confirmResync: confirmArchiveResync });
      await refresh();
      setArchiveHasContent(true);
    } catch (err) {
      if (!err?.canceled) onError(`Archive failed: ${err.message}`);
    } finally {
      setLoading(false);
      setArchiveProgress(null);
    }
  };

  const onBulkArchiveSurveys = async (surveyIds) => {
    if (!surveyIds || surveyIds.length === 0) return;

    if (!archiveFileOpen) {
      onError("No archive file open. Please go to Accounts → Archives and open an archive file first.");
      openAccountPanel('archive');
      return;
    }

    setLoading(true);
    onError('');

    const total = surveyIds.length;
    setArchiveProgress({ current: 0, total, archived: 0, failed: 0 });

    try {
      // Validate: only Ready surveys may be archived (WP3 §7). Deployed / Staged
      // / Sent surveys must be undeployed first.
      const notReady = surveyIds.filter(sid => {
        const st = surveyStatusById.get(sid);
        return st && st !== LIFECYCLE_READY;
      });
      if (notReady.length > 0) {
        throw new Error(`Cannot archive ${notReady.length} survey(s) that are not Ready. Undeploy them first — only Ready surveys can be archived.`);
      }

      await archiveWithOverwriteCheck({ survey_ids: surveyIds }, (p) => {
        setArchiveProgress({ current: 0, total, archived: Math.floor(total * p.percent / 100), failed: 0 });
      }, { confirmOverwrite: confirmArchiveOverwrite, confirmResync: confirmArchiveResync });

      await refresh();
      setArchiveHasContent(true);
    } catch (err) {
      if (!err?.canceled) onError(`Bulk archive failed: ${err.message}`);
    } finally {
      setLoading(false);
      setArchiveProgress(null);
    }
  };

  // ('Load Archive' removed 2026-08-15, verified dead in testing: the handler
  // never completed a load. Survey restoration deliberately lives in the
  // Accounts panel's Archive Manager — the selective-restore "Reload" flow via
  // sam-dbam — so restoring is an explicit, deliberate act by the Author.)

  return {
    archiveFileOpen, setArchiveFileOpen,
    archiveHasContent, setArchiveHasContent,
    archiveProgress,
    overwriteModal, setOverwriteModal,
    onArchiveSurvey,
    onBulkArchiveSurveys,
  };
}
